import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface StructuredDataProps {
  language: string;
}

const StructuredData: React.FC<StructuredDataProps> = ({ language }) => {
  const location = useLocation();
  const path = location.pathname.substring(1) || 'Home';
  const capitalizedPath = path.charAt(0).toUpperCase() + path.slice(1);
  
  const pageTitle = capitalizedPath === 'Home' 
    ? "Tai Nguyen | Software Engineer Portfolio" 
    : `${capitalizedPath} | Tai Nguyen Software Engineer`;
    
  const metaDesc = language === 'en'
    ? `Explore the ${capitalizedPath} section of Tai Nguyen's Software Engineering and Unity Development portfolio.`
    : `Khám phá phần ${capitalizedPath} trong hồ sơ năng lực Kỹ sư Phần mềm và Phát triển Game của Tai Nguyen.`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Nguyen Ngoc Thanh Tai",
    "alternateName": ["Tai Nguyen", "tainguyen", "ntainguyen", "ntainguyenit"],
    "jobTitle": language === 'en' ? "Game Developer & Unity Developer" : "Nhà Phát Triển Game & Unity Developer",
    "description": language === 'en'
      ? "IT Student specializing in C# programming, Unity game development, and interactive experiences"
      : "Sinh viên CNTT chuyên về lập trình C#, phát triển game Unity và trải nghiệm tương tác",
    "url": "https://ntainguyenit.pages.dev",
    "image": "https://ntainguyenit.pages.dev/author-avatar-rounded.webp",
    "email": "ntainguyen.it@gmail.com",
    "telephone": "+84366063944",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hue",
      "addressCountry": "Vietnam"
    },
    "alumniOf": {
      "@type": "EducationalOrganization",
      "name": language === 'en' ? "Hue University of Sciences" : "Đại học Khoa học Huế"
    },
    "knowsAbout": [
      "Game Development",
      "Unity 3D",
      "C# Programming",
      "Mobile Game Development",
      "Web Game Development",
      "UI/UX Design",
      "3D Modeling",
      "Blender"
    ],
    "sameAs": [
      "https://github.com/ntainguyenit",
      "https://linkedin.com/in/ntainguyenit",
      "https://t.me/ntainguyenit"
    ],
    "worksFor": {
      "@type": "EducationalOrganization",
      "name": language === 'en' ? "Hue University of Sciences" : "Đại học Khoa học Huế"
    }
  };

  const websiteData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Tai Nguyen Portfolio",
    "alternateName": ["tainguyen portfolio", "tai nguyen portfolio", "ntainguyen.it", "Tai Nguyen - Game Developer Portfolio"],
    "url": "https://ntainguyenit.pages.dev",
    "description": language === 'en'
      ? "Portfolio website showcasing game development projects, Unity skills, and C# programming expertise"
      : "Website portfolio giới thiệu các dự án phát triển game, kỹ năng Unity và chuyên môn lập trình C#",
    "inLanguage": [language === 'en' ? "en-US" : "vi-VN"],
    "author": {
      "@type": "Person",
      "name": "Tai Nguyen"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://ntainguyenit.pages.dev/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const sitelinksData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "SiteNavigationElement",
        "position": 1,
        "name": language === 'en' ? "Home" : "Trang Chủ",
        "description": language === 'en' ? "Welcome to Tai Nguyen's portfolio" : "Chào mừng đến với portfolio của Tai Nguyen",
        "url": "https://ntainguyenit.pages.dev/Home"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 2,
        "name": language === 'en' ? "About" : "Giới Thiệu",
        "description": language === 'en' ? "Learn about Tai Nguyen's background and education" : "Tìm hiểu về Tai Nguyen",
        "url": "https://ntainguyenit.pages.dev/About"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 3,
        "name": language === 'en' ? "Skills" : "Kỹ Năng",
        "description": language === 'en' ? "Technical skills in Unity, C#, and game development" : "Kỹ năng về Unity, C# và phát triển game",
        "url": "https://ntainguyenit.pages.dev/Skills"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 4,
        "name": language === 'en' ? "Projects" : "Dự Án",
        "description": language === 'en' ? "Game development projects and portfolio showcase" : "Các dự án game và portfolio",
        "url": "https://ntainguyenit.pages.dev/Projects"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 5,
        "name": language === 'en' ? "Contact" : "Liên Hệ",
        "description": language === 'en' ? "Get in touch with Tai Nguyen" : "Liên hệ với Tai Nguyen",
        "url": "https://ntainguyenit.pages.dev/Contact"
      }
    ]
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": language === 'en' ? "Home" : "Trang Chủ",
        "item": "https://ntainguyenit.pages.dev"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": language === 'en' ? "Portfolio" : "Portfolio",
        "item": "https://ntainguyenit.pages.dev#projects"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDesc} />
      </Helmet>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData)
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(websiteData)
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(sitelinksData)
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbData)
        }}
      />
    </>
  );
};

export default StructuredData;